import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { Plus, Pencil, Search } from 'lucide-react';
import DataTable from 'src/components/ui/data-table';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';

import { useToast } from 'src/hooks/use-toast';
import { useAppSettings } from 'src/services/api/queries/appSettingsQueries';
import {
  useCreateAppSetting,
  useUpdateAppSetting,
} from 'src/services/api/mutations/appSettingsMutations';

const InlineEditInput = ({ value, onChange, onKeyDown, className, style }) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <input
      ref={ref}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      className={className}
      style={style}
    />
  );
};

const FormField = ({
  id,
  label,
  children,
  className = '',
  required = false,
}) => (
  <div className={className}>
    <Label
      htmlFor={id}
      className="block text-[12.5px] font-[560] leading-none text-foreground-strong tracking-[0.005em] mb-1.5"
    >
      {label}
      {required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const DEFAULT_PAGE_SIZE = 10;

const ApplicationSettings = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newSetting, setNewSetting] = useState({
    parameter_name: '',
    parameter_value: '',
    description: '',
  });

  const { data: settingsData, isLoading } = useAppSettings();
  const createSettingMutation = useCreateAppSetting();
  const updateSettingMutation = useUpdateAppSetting();

  const allSettings = settingsData?.data?.items || [];

  const settings = useMemo(() => {
    if (!searchQuery.trim()) return allSettings;
    const q = searchQuery.toLowerCase();
    return allSettings.filter(
      (s) =>
        s.parameter_name?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q) ||
        String(s.parameter_value)?.toLowerCase().includes(q)
    );
  }, [allSettings, searchQuery]);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  React.useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
  }, []);

  const totalItems = settings.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / currentPageSize));
  const pagedSettings = useMemo(() => {
    const start = (currentPage - 1) * currentPageSize;
    return settings.slice(start, start + currentPageSize);
  }, [settings, currentPage, currentPageSize]);

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: parseInt(newPage, 10) });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: parseInt(newPageSize, 10), page: 1 });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    updateUrlParams({ page: 1 });
  };

  const handleCreateSetting = async (e) => {
    e.preventDefault();
    try {
      await createSettingMutation.mutateAsync(newSetting);
      setIsCreateDialogOpen(false);
      setNewSetting({
        parameter_name: '',
        parameter_value: '',
        description: '',
      });
      toast({ message: 'Setting created successfully', variant: 'success' });
    } catch (err) {
      toast({
        message: err.message || 'Failed to create setting',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateValue = async (parameterName, newValue) => {
    try {
      await updateSettingMutation.mutateAsync({
        parameter: parameterName,
        data: newValue,
      });
      setEditingValue(null);
      toast({ message: 'Setting updated successfully', variant: 'success' });
    } catch (err) {
      toast({
        message: 'Failed to update setting. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const isFormValid =
    newSetting.parameter_name.trim() &&
    newSetting.parameter_value.trim() &&
    newSetting.description.trim();

  const columns = [
    {
      key: 'parameter_name',
      header: 'Parameter Name',
      width: '26%',
      minWidth: '160px',
      className: 'text-left',
      render: (value) => (
        <span className="font-mono text-[12.5px] font-semibold text-foreground-strong tracking-[0.02em] break-all">
          {value}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      width: '38%',
      minWidth: '200px',
      className: 'text-left',
      render: (value) => (
        <span className="text-[12.5px] text-muted-foreground font-[450] leading-relaxed">
          {value}
        </span>
      ),
    },
    {
      key: 'parameter_value',
      header: 'Value',
      width: '28%',
      minWidth: '140px',
      className: 'text-left',
      render: (_value, row) =>
        editingValue?.parameter_name === row.parameter_name ? (
          <div className="flex flex-col gap-1.5">
            <InlineEditInput
              value={editingValue.value}
              onChange={(e) =>
                setEditingValue((prev) => ({ ...prev, value: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter')
                  handleUpdateValue(row.parameter_name, editingValue.value);
                if (e.key === 'Escape') setEditingValue(null);
              }}
              className="w-full px-2.5 py-1.5 rounded-[7px] border border-primary text-[12.5px] text-foreground-strong font-[inherit] outline-none bg-background"
              style={{ boxShadow: '0 0 0 3px rgba(32, 104, 178,.12)' }}
            />
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                type="button"
                onClick={() =>
                  handleUpdateValue(row.parameter_name, editingValue.value)
                }
              >
                Save
              </Button>
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={() => setEditingValue(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="text-left w-full cursor-pointer bg-transparent border-0 p-0 text-[13px] text-foreground-strong break-all"
            onClick={() =>
              setEditingValue({
                parameter_name: row.parameter_name,
                value: row.parameter_value,
              })
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setEditingValue({
                  parameter_name: row.parameter_name,
                  value: row.parameter_value,
                });
              }
            }}
          >
            {row.parameter_value}
          </button>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      className: 'text-right',
      render: (_value, row) => (
        <button
          type="button"
          title="Edit value"
          className="w-7 h-7 rounded-[6px] border-none bg-transparent text-muted-foreground inline-flex items-center justify-center cursor-pointer transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-border hover:text-muted-foreground"
          onClick={() =>
            setEditingValue({
              parameter_name: row.parameter_name,
              value: row.parameter_value,
            })
          }
        >
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>
      ),
    },
  ];

  const emptyStateNode = (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
        <div
          className="w-18 h-18 rounded-full bg-border flex items-center justify-center mb-5"
          style={{ width: 72, height: 72 }}
        >
          <Search className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
        </div>
        <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
          No settings found
        </p>
        <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs">
          {searchQuery
            ? 'Try adjusting your search or add a new setting parameter.'
            : 'No application settings have been configured yet.'}
        </p>
      </div>
    </div>
  );

  return (
    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Create New Setting</DialogTitle>
          <DialogDescription>
            Add a new application configuration parameter
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleCreateSetting}
          className="flex flex-col flex-1 min-h-0"
        >
          <div className="px-[22px] py-[18px] space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField id="parameter_name" label="Parameter Name" required>
                <Input
                  id="parameter_name"
                  placeholder="e.g. MAX_UPLOAD_SIZE"
                  value={newSetting.parameter_name}
                  onChange={(e) =>
                    setNewSetting((prev) => ({
                      ...prev,
                      parameter_name: e.target.value,
                    }))
                  }
                  required
                />
              </FormField>
              <FormField id="parameter_value" label="Value" required>
                <Input
                  id="parameter_value"
                  placeholder="e.g. 10MB"
                  value={newSetting.parameter_value}
                  onChange={(e) =>
                    setNewSetting((prev) => ({
                      ...prev,
                      parameter_value: e.target.value,
                    }))
                  }
                  required
                />
              </FormField>
            </div>
            <FormField id="description" label="Description" required>
              <Textarea
                id="description"
                placeholder="Describe what this parameter controls..."
                value={newSetting.description}
                onChange={(e) =>
                  setNewSetting((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="min-h-[80px] resize-y leading-relaxed"
                required
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createSettingMutation.isPending || !isFormValid}
            >
              {createSettingMutation.isPending ? 'Creating…' : 'Create Setting'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <div>
        <PageHeader
          title="Application Settings"
          actions={
            <DialogTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-[border-color,color,background-color] cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
                Add Setting
              </button>
            </DialogTrigger>
          }
        />

        <PageContainer>
          {/* Filter bar */}
          <div className="flex items-center gap-2.5 mb-4">
            <div className="relative">
              <Search
                className="absolute left-[11px] top-1/2 -translate-y-1/2 w-[15px] h-[15px] text-muted-foreground pointer-events-none"
                strokeWidth={1.8}
              />
              <input
                type="text"
                placeholder="Search parameters..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-[34px] pr-3 py-2 rounded-lg border border-input text-[12.5px] text-foreground-strong bg-card outline-none w-60 transition-[border-color,box-shadow] placeholder:text-muted-foreground focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]"
              />
            </div>
            <div className="flex-1" />
            {searchQuery && (
              <span className="text-[12px] text-muted-foreground">
                {totalItems} of {allSettings.length} parameters
              </span>
            )}
            {!searchQuery && allSettings.length > 0 && (
              <span className="text-[12px] text-muted-foreground">
                {allSettings.length} parameters
              </span>
            )}
          </div>

          <DataTable
            className="rounded-xl overflow-hidden"
            data={pagedSettings}
            columns={columns}
            loading={isLoading}
            emptyState={emptyStateNode}
            pagination={totalItems > currentPageSize}
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={currentPageSize}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </PageContainer>
      </div>
    </Dialog>
  );
};

export default ApplicationSettings;
